import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { isLevelUnlocked, getLevelHighScore, isLevelCompleted, getSelectedDifficulty, setSelectedDifficulty } from '@/config/progress';

const MESSAGES = [
  'SOS',
  'TWO KEYS',
  'NO MOVEMENT',
  'LIKE A GOLEM',
  'I HEART NES',
  'PROPS TO FELDO',
  'INTERESTING GAMEJAM',
  'ELECTRIC MESSAGES',
  'SHORT AND LONG STRIDES',
  'YOU ARE MY FAVORITE JUDGE',
];

interface LevelSelectInit {
  levelIndex?: number;
}

export class LevelSelectScene extends Phaser.Scene {
  private selectedLevel: number = 0;
  private selectedDifficulty: string = 'easy';
  private levelBackgrounds: Phaser.GameObjects.Rectangle[] = [];
  private difficultyButtons: Phaser.GameObjects.Rectangle[] = [];
  private difficultyTexts: Phaser.GameObjects.Text[] = [];
  private initData: LevelSelectInit | null = null;
  private listContainer!: Phaser.GameObjects.Container;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private scrollbarThumb!: Phaser.GameObjects.Rectangle;
  private scrollbarTrack!: Phaser.GameObjects.Rectangle;
  private listHeight: number = 0;

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  init(data: LevelSelectInit): void {
    this.initData = data || null;

    // Always read difficulty from localStorage (source of truth)
    this.selectedDifficulty = getSelectedDifficulty();
    this.scrollY = 0;

    // Level can be passed in (e.g., from ResultScene for "Play Next")
    this.selectedLevel = this.initData?.levelIndex ?? 0;

    // Ensure selected level is unlocked, otherwise select first unlocked
    if (!isLevelUnlocked(this.selectedLevel)) {
      this.selectedLevel = MESSAGES.findIndex((_, i) => isLevelUnlocked(i));
      if (this.selectedLevel < 0) this.selectedLevel = 0;
    }
  }

  create(): void {
    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);

    // Layout constants
    const panelTop = 200;
    const panelHeight = 300;
    const panelBottom = panelTop + panelHeight;

    // Panel frame - contains only the level list
    const frame = this.add.rectangle(GAME_WIDTH / 2, panelTop + panelHeight / 2, 760, panelHeight, THEME.colors.backgroundLightHex, 0.82);
    frame.setStrokeStyle(3, THEME.colors.magentaHex);
    frame.setDepth(-1);

    // Title - ABOVE the purple panel
    const title = this.add.text(GAME_WIDTH / 2, 100, 'Select a message, difficulty,\nand click Play', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.title,
      color: THEME.colors.cyan,
      align: 'center',
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 15);

    // Column headers - inside panel near top
    this.add.text(350, panelTop + 25, 'MESSAGE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);

    this.add.text(880, panelTop + 25, 'HIGH SCORE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5, 0.5);

    // Scrollable list area - inside panel
    const listTop = panelTop + 50;
    this.listHeight = panelHeight - 60;

    // Mask for the list
    const maskShape = this.add.rectangle(GAME_WIDTH / 2, listTop + this.listHeight / 2, 620, this.listHeight, 0xffffff);
    maskShape.setVisible(false);
    const mask = maskShape.createGeometryMask();

    this.listContainer = this.add.container(0, 0);
    this.listContainer.setMask(mask);

    this.createLevelList(listTop);

    // Calculate max scroll
    const rowSpacing = 55;
    const totalHeight = MESSAGES.length * rowSpacing;
    this.maxScroll = Math.max(0, totalHeight - this.listHeight);

    this.createScrollbar(listTop, this.listHeight);

    // Scroll input
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });

    // Difficulty section - positioned below the panel with equal spacing to play button
    const difficultyY = panelBottom + 30;
    this.add.text(GAME_WIDTH / 2, difficultyY, 'DIFFICULTY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.createDifficultySelector(difficultyY + 35);

    // Play button - below difficulty with equal spacing
    this.createButton(GAME_WIDTH / 2, difficultyY + 105, 'PLAY', () => {
      this.transitionTo('GameScene', {
        levelIndex: this.selectedLevel,
        message: MESSAGES[this.selectedLevel],
        difficulty: this.selectedDifficulty,
      });
    });

    // Back button
    this.createButton(100, 60, '← BACK', () => {
      this.transitionTo('MenuScene');
    }, true);
  }

  private createLevelList(startY: number): void {
    const rowSpacing = 55;
    const rowHeight = 50;
    this.levelBackgrounds = [];

    MESSAGES.forEach((message, index) => {
      const y = startY + index * rowSpacing + rowHeight / 2;

      const unlocked = isLevelUnlocked(index);
      const completed = isLevelCompleted(index);
      const highScore = getLevelHighScore(index);

      const bgColor = unlocked ? THEME.colors.backgroundLightHex : 0x1a1a2e;
      const bg = this.add.rectangle(GAME_WIDTH / 2, y, 620, rowHeight, bgColor, 1);
      const stroke = index === this.selectedLevel && unlocked ? THEME.colors.cyanHex : THEME.colors.borderHex;
      bg.setStrokeStyle(2, stroke);
      this.levelBackgrounds.push(bg);

      this.listContainer.add(bg);

      if (unlocked) {
        const titleText = this.add.text(350, y, message, {
          fontFamily: THEME.fonts.heading,
          fontSize: THEME.sizes.body,
          color: THEME.colors.text,
        }).setOrigin(0, 0.5);
        this.listContainer.add(titleText);

        if (highScore > 0) {
          const scoreText = this.add.text(880, y, highScore.toLocaleString(), {
            fontFamily: THEME.fonts.body,
            fontSize: THEME.sizes.small,
            color: THEME.colors.yellow,
          }).setOrigin(0.5, 0.5);
          this.listContainer.add(scoreText);
        }

        if (completed) {
          const checkmark = this.add.text(960, y, '✓', {
            fontFamily: THEME.fonts.heading,
            fontSize: THEME.sizes.body,
            color: THEME.colors.green,
          }).setOrigin(0.5);
          this.listContainer.add(checkmark);
        }

        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => {
          this.selectedLevel = index;
          this.updateLevelSelection();
        });
      } else {
        const lockIcon = this.add.text(350, y, '🔒', {
          fontFamily: THEME.fonts.heading,
          fontSize: THEME.sizes.body,
        }).setOrigin(0, 0.5);
        this.listContainer.add(lockIcon);
      }
    });
  }

  private updateLevelSelection(): void {
    this.levelBackgrounds.forEach((bg, index) => {
      const unlocked = isLevelUnlocked(index);
      const isSelected = index === this.selectedLevel && unlocked;
      bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
    });
  }

  private createScrollbar(listTop: number, listHeight: number): void {
    const scrollbarX = GAME_WIDTH / 2 + 320;
    const buttonSize = 20;
    const trackHeight = listHeight - buttonSize * 2 - 10;
    const trackY = listTop + listHeight / 2;

    // Up button
    const upBtn = this.add.rectangle(scrollbarX, listTop + buttonSize / 2 + 5, buttonSize, buttonSize, THEME.colors.backgroundPanelHex, 0.8);
    upBtn.setStrokeStyle(1, THEME.colors.borderHex);
    const upArrow = this.add.text(scrollbarX, listTop + buttonSize / 2 + 5, '▲', {
      fontFamily: THEME.fonts.body,
      fontSize: '12px',
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    upBtn.setInteractive({ useHandCursor: true });
    upBtn.on('pointerdown', () => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY - 55, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });
    upBtn.on('pointerover', () => upArrow.setColor(THEME.colors.text));
    upBtn.on('pointerout', () => upArrow.setColor(THEME.colors.cyan));

    // Down button
    const downBtn = this.add.rectangle(scrollbarX, listTop + listHeight - buttonSize / 2 - 5, buttonSize, buttonSize, THEME.colors.backgroundPanelHex, 0.8);
    downBtn.setStrokeStyle(1, THEME.colors.borderHex);
    const downArrow = this.add.text(scrollbarX, listTop + listHeight - buttonSize / 2 - 5, '▼', {
      fontFamily: THEME.fonts.body,
      fontSize: '12px',
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    downBtn.setInteractive({ useHandCursor: true });
    downBtn.on('pointerdown', () => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + 55, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });
    downBtn.on('pointerover', () => downArrow.setColor(THEME.colors.text));
    downBtn.on('pointerout', () => downArrow.setColor(THEME.colors.cyan));

    // Scrollbar track (background)
    this.scrollbarTrack = this.add.rectangle(scrollbarX, trackY, 8, trackHeight, THEME.colors.backgroundPanelHex, 0.5);
    this.scrollbarTrack.setStrokeStyle(1, THEME.colors.borderHex);

    // Calculate thumb size based on visible ratio
    const rowSpacing = 55;
    const totalHeight = MESSAGES.length * rowSpacing;
    const visibleRatio = Math.min(1, listHeight / totalHeight);
    const thumbHeight = Math.max(30, trackHeight * visibleRatio);

    // Scrollbar thumb
    this.scrollbarThumb = this.add.rectangle(scrollbarX, trackY - trackHeight / 2 + thumbHeight / 2, 8, thumbHeight, THEME.colors.cyanHex, 0.8);

    // Make thumb draggable
    this.scrollbarThumb.setInteractive({ useHandCursor: true, draggable: true });

    const minThumbY = trackY - trackHeight / 2 + thumbHeight / 2;
    const maxThumbY = trackY + trackHeight / 2 - thumbHeight / 2;

    this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject, _dragX: number, dragY: number) => {
      if (gameObject === this.scrollbarThumb) {
        const clampedY = Phaser.Math.Clamp(dragY, minThumbY, maxThumbY);
        this.scrollbarThumb.y = clampedY;

        // Calculate scroll position from thumb position
        const thumbProgress = (clampedY - minThumbY) / (maxThumbY - minThumbY);
        this.scrollY = thumbProgress * this.maxScroll;
        this.listContainer.y = -this.scrollY;
      }
    });
  }

  private updateScrollbar(): void {
    const trackHeight = this.listHeight - 20;
    const rowSpacing = 55;
    const totalHeight = MESSAGES.length * rowSpacing;
    const visibleRatio = Math.min(1, this.listHeight / totalHeight);
    const thumbHeight = Math.max(30, trackHeight * visibleRatio);

    const minThumbY = this.scrollbarTrack.y - trackHeight / 2 + thumbHeight / 2;
    const maxThumbY = this.scrollbarTrack.y + trackHeight / 2 - thumbHeight / 2;

    const scrollProgress = this.scrollY / this.maxScroll;
    this.scrollbarThumb.y = minThumbY + scrollProgress * (maxThumbY - minThumbY);
  }

  private createDifficultySelector(y: number): void {
    const difficulties = ['easy', 'medium', 'hard', 'expert'];
    const startX = GAME_WIDTH / 2 - 180;
    this.difficultyButtons = [];
    this.difficultyTexts = [];

    difficulties.forEach((diff, index) => {
      const x = startX + index * 120;
      const isSelected = diff === this.selectedDifficulty;

      const btn = this.add.rectangle(x, y, 100, 40, THEME.colors.backgroundPanelHex, 1);
      btn.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      this.difficultyButtons.push(btn);

      const text = this.add.text(x, y, diff.toUpperCase(), {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.tiny,
        color: isSelected ? THEME.colors.text : THEME.colors.textMuted,
      }).setOrigin(0.5);
      this.difficultyTexts.push(text);

      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.selectedDifficulty = diff;
        setSelectedDifficulty(diff);
        this.updateDifficultySelection();
      });
    });
  }

  private updateDifficultySelection(): void {
    const difficulties = ['easy', 'medium', 'hard', 'expert'];
    this.difficultyButtons.forEach((btn, index) => {
      const isSelected = difficulties[index] === this.selectedDifficulty;
      btn.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      this.difficultyTexts[index].setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
    });
  }

  private createButton(x: number, y: number, text: string, onClick: () => void, small = false): void {
    const width = small ? 120 : 200;
    const height = small ? 40 : 50;
    const fontSize = small ? THEME.sizes.small : THEME.sizes.bodyLarge;

    const isPrimary = !small;
    const fillColor = isPrimary ? THEME.colors.magentaHex : THEME.colors.backgroundLightHex;
    const strokeColor = isPrimary ? THEME.colors.magentaDarkHex : THEME.colors.cyanHex;
    const textColor = isPrimary ? THEME.colors.text : THEME.colors.cyan;
    const hoverFill = isPrimary ? THEME.colors.magentaDarkHex : THEME.colors.backgroundPanelHex;

    const bg = this.add.rectangle(x, y, width, height, fillColor, 1);
    bg.setStrokeStyle(2, strokeColor);

    this.add.text(x, y, text, {
      fontFamily: THEME.fonts.heading,
      fontSize,
      color: textColor,
    }).setOrigin(0.5);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(hoverFill));
    bg.on('pointerout', () => bg.setFillStyle(fillColor));
    bg.on('pointerdown', onClick);
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }
}
